import React, { useState, useRef, useEffect } from 'react';
import * as if_block from "../bfm_if_flow_block";
import * as ai_block from "../bfm_ai_flow_block";
import * as execute_block from "../bfm_execute_flow_block";
import * as set_block from "../bfm_set_flow_block";
import * as db_block from "../bfm_db_flow_block";
import * as notes_block from "../bfm_notes_flow_block";
import * as macro_block from "../bfm_macro_flow_block";


// The categories and their respective sub-buttons with FontAwesome icon classes
const toolbarCategories = {
    flow: {
        icon: "fa-solid fa-circle-arrow-right",
        label: "Flow",
        color: "text-primary",
        buttons: [
            { id: "if", label: "If", action: "addIf", icon: "fa-solid fa-code-branch" },
            { id: "execFlow", label: "ExecFlow", action: "addExecFlow", icon: "fa-solid fa-play" },
            { id: "macro", label: "Macro", action: "addMacro", icon: "fa-solid fa-play" },
        ]
    },
    utility: {
        icon: "fa-solid fa-wrench",
        label: "Utility",
        color: "text-purple",
        buttons: [
            { id: "note", label: "Note", action: "addNote", icon: "fa-solid fa-indent" },
        ]
    },
    api: {
        icon: "fa-solid fa-code",
        label: "API",
        color: "text-danger",
        buttons: [
            { id: "api-1", label: "prova", action: "restCall", icon: "fa-solid fa-globe" },
        ]
    },
    ai: {
        icon: "fa-solid fa-microchip",
        label: "AI",
        color: "text-info",
        buttons: [
            { id: "aiAction", label: "aiAction", action: "addAiAction", icon: "fa-solid fa-comment-dots" }
        ]
    },
    database: {
        icon: "fa-solid fa-database",
        label: "Database",
        color: "text-secondary",
        buttons: [
            { id: "dbQuery", label: "DbQuery", action: "addDbQuery", icon: "fa-solid fa-plus" },
        ]
    },
    get: {
        icon: "fa-solid fa-download",
        label: "Get/Set",
        color: "text-success",
        buttons: [
            { id: "setVariable", label: "SetVariable", action: "addSetVariable", icon: "fa-solid fa-sliders-h" }

        ]
    },
};

const BFM_Toolbar = (params) => {
    const { onCommand } = params;

    const [position, setPosition] = useState({ x: 250, y: 110 });
    const [dragging, setDragging] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const [hoveredButton, setHoveredButton] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const toolbarRef = useRef(null);
    const dragOffsetRef = useRef({ x: 0, y: 0 });

    // Handle dragging
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (dragging && toolbarRef.current) {
                setPosition({
                    x: e.clientX - dragOffsetRef.current.x,
                    y: e.clientY - dragOffsetRef.current.y
                });
            }
        };

        const handleMouseUp = () => {
            setDragging(false);
        };

        if (dragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging]);

    const startDragging = (e) => {
        if (toolbarRef.current) {
            const rect = toolbarRef.current.getBoundingClientRect();
            dragOffsetRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
            setDragging(true);
        }
    };

    const handleCategoryClick = (category) => {
        setActiveCategory(activeCategory === category ? null : category);
    };

    const handleButtonClick = (buttonId, action) => {
        console.log(`Button clicked: ${buttonId} - Action: ${action}`);
        // Here you can implement the specific action for each button
        // For example:
        switch (action) {
            case 'addIf':
                onCommand({ command: "add_block", argument: { type: "if" } })
                break;
            case 'addSetVariable':
                onCommand({ command: "add_block", argument: { type: "if" } })
                break;
            case 'addExecFlow':
                onCommand({ command: "add_block", argument: { type: "execute" } })
                break;
            case 'addNote':
                onCommand({ command: "add_block", argument: { type: "notes" } })                
                break;
            case 'addMacro':
                onCommand({ command: "add_block", argument: { type: "macro" } })
                break;
            case 'addAiAction':
                onCommand({ command: "add_block", argument: { type: "ai" } })
                break;
            case 'addDbQuery':
                onCommand({ command: "add_block", argument: { type: "db" } })
                break;
            default:
                console.log('Unknown action:', action);
        }
    };


    return (
        <div
            ref={toolbarRef}
            className="position-fixed"
            style={{
                left: position.x,
                top: position.y,
                zIndex: 1050,
                userSelect: 'none'
            }}
        >
            {/* Main toolbar */}
            <div className="card shadow-sm" style={{ width: '20em', maxWidth: '100%' }}>
                <div
                    className="card-header bg-light d-flex justify-content-between align-items-center py-2"
                    onMouseDown={startDragging}
                    style={{ cursor: 'move' }}
                >
                    <span className="small fw-medium text-secondary">Toolbar</span>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                    >
                        <i className={`fa-solid ${isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
                    </button>
                </div>
                {!isCollapsed && (
                    <>
                        <div className="card-body p-1 d-flex flex-wrap gap-1">
                            {Object.entries(toolbarCategories).map(([key, category]) => {
                                const isActive = activeCategory === key;

                                return (
                                    <div key={key} className="position-relative"
                                        onMouseEnter={() => setHoveredButton(key)}
                                        onMouseLeave={() => setHoveredButton(null)}>
                                        <button
                                            className={`btn btn-sm ${isActive ? 'btn-light' : 'btn-outline-light'} p-2`}
                                            onClick={() => handleCategoryClick(key)}
                                        >
                                            <i className={`${category.icon} ${category.color}`}></i>
                                        </button>
                                        {hoveredButton === key && (
                                            <div className="position-absolute bg-dark text-white px-2 py-1 rounded"
                                                style={{
                                                    bottom: '-25px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'nowrap',
                                                    zIndex: 1060
                                                }}>
                                                {category.label}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Secondary toolbar for active category */}
                        {activeCategory && (
                            <div className="card-body p-2 border-top">
                                <div className="small fw-medium mb-2 pb-1 border-bottom d-flex align-items-center gap-2">
                                    <i className={`${toolbarCategories[activeCategory].icon} ${toolbarCategories[activeCategory].color}`}></i>
                                    {toolbarCategories[activeCategory].label}
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                    {toolbarCategories[activeCategory].buttons.map((button) => (
                                        <button
                                            key={button.id}
                                            className="btn btn-sm btn-outline-light text-secondary d-flex align-items-center gap-1"
                                            onClick={() => handleButtonClick(button.id, button.action)}
                                        >
                                            <i className={`${button.icon} me-1`} />
                                            {button.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BFM_Toolbar;